import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { isUuid } from '~/utils';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import type {
  SellerCatalogPriceFiltersInput,
  SellerProductFiltersInput,
} from '~/graphql/graphql';

export const useGetMyProducts = (
  page: number,
  size: number,
  filters?: SellerProductFiltersInput,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const productsQuery = graphql(`
    query sellerProductInventories(
      $page: Int!
      $size: Int!
      $filters: SellerProductFiltersInput
    ) {
      sellerProductInventories(
        pageInput: { page: $page, size: $size }
        filters: $filters
      ) {
        paging {
          totalPages
          totalElements
          size
          number
        }
        content {
          id
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          location {
            id
            name
            code
            facility {
              id
              address {
                addressLine1
                city
                region
              }
            }
          }
          sellerProductConfiguration {
            id
            scope
            facility {
              id
              code
            }
            criticalStockLevel(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            internalNotes
            lowStockLevel(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            readyToShipDays
            replenishmentRatePerWeek(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            replenishmentRateStored: replenishmentRatePerWeek(
              mode: { mode: STORED }
            ) {
              value
              unit
            }
            minimumOrderQuantity(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            status
            unit
          }
          unitPrice {
            price {
              amount
              currency
            }
            unit
          }
          platformProductsLength {
            id
            isNominal
            lengthDisplayValue
            length {
              id
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              code
              category
              actualWidthInches
              actualThicknessInches
              nominalWidthInches
              nominalThicknessInches
              actualLengthInches
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    productsQuery,
    [
      'my-products',
      page.toString(),
      size.toString(),
      JSON.stringify(filters || null),
    ],
    { page, size, filters },
    options
  );
};

export const useGetProductLengthsByProductId = (inventoryId: string) => {
  const productLengthsQuery = graphql(`
    query GetProductLengthsByProductId($inventoryId: ID!) {
      sellerProductInventory(id: $inventoryId) {
        id
        quantity {
          value
          unit
        }
        location {
          id
          facility {
            id
            organization {
              id
              defaultCurrency
            }
          }
        }
        platformProductsLength {
          id
          isNominal
          lengthDisplayValue
          length {
            id
            actualLengthInches
            nominalLengthInches
          }
          latestPriceMid {
            amount
            currency
          }
          platformProduct {
            id
            name
            category
            materialCode
            constructionCode
            code
            properties
            actualWidthInches
            actualThicknessInches
            nominalWidthInches
            nominalThicknessInches
            actualLengthInches
          }
        }
        sellerProductConfiguration {
          id
          scope
          facility {
            id
            code
          }
          internalNotes
          replenishmentRatePerWeek {
            value
            unit
          }
          readyToShipDays
          criticalStockLevel {
            value
            unit
          }
          criticalStockLevelInPieces: criticalStockLevel(
            mode: { mode: SPECIFIC, unit: PCS }
          ) {
            value
            unit
          }
          lowStockLevel {
            value
            unit
          }
          lowStockLevelInPieces: lowStockLevel(
            mode: { mode: SPECIFIC, unit: PCS }
          ) {
            value
            unit
          }
          minimumOrderQuantity {
            value
            unit
          }
          status
          unit
        }
      }
    }
  `);

  return useGraphqlQuery(
    productLengthsQuery,
    ['my-products', inventoryId],
    { inventoryId },
    { enabled: isUuid(inventoryId) }
  );
};

export const useUpsertSellerInventory = () => {
  const upsertInventoryMutation = graphql(`
    mutation UpsertSellerInventory(
      $platformProductsLengthId: ID!
      $locationId: ID!
      $quantity: QuantityInput!
    ) {
      upsertSellerInventory(
        platformProductsLengthId: $platformProductsLengthId
        locationId: $locationId
        quantity: $quantity
      ) {
        id
        quantity {
          value
          unit
        }
      }
    }
  `);

  return useGraphqlMutation(upsertInventoryMutation, [
    'my-products',
    'sellerProductInventories',
  ]);
};

export const useUpsertSellerProductConfiguration = () => {
  const upsertProductConfigMutation = graphql(`
    mutation UpsertSellerProductConfiguration(
      $platformProductLengthId: ID!
      $scope: SellerProductConfigurationScope
      $facilityId: ID
      $sellerProductConfigurationInput: SellerProductConfigurationInput!
    ) {
      upsertSellerProductConfiguration(
        platformProductLengthId: $platformProductLengthId
        scope: $scope
        facilityId: $facilityId
        sellerProductConfigurationInput: $sellerProductConfigurationInput
      ) {
        id
        scope
        facility {
          id
          code
        }
        criticalStockLevel {
          value
          unit
        }
        internalNotes
        lowStockLevel {
          value
          unit
        }
        minimumOrderQuantity {
          value
          unit
        }
        readyToShipDays
        replenishmentRatePerWeek {
          value
          unit
        }
        status
      }
    }
  `);

  return useGraphqlMutation(upsertProductConfigMutation, [
    'my-products',
    'sellerProductInventories',
  ]);
};

export const useUpsertSellerInventoryAndProductConfiguration = () => {
  const upsertInventoryAndConfigMutation = graphql(`
    mutation UpsertSellerInventoryAndProductConfiguration(
      $platformProductsLengthId: ID!
      $locationId: ID!
      $scope: SellerProductConfigurationScope
      $facilityId: ID
      $quantity: QuantityInput!
      $sellerProductConfigurationInput: SellerProductConfigurationInput!
    ) {
      upsertSellerInventory(
        platformProductsLengthId: $platformProductsLengthId
        locationId: $locationId
        quantity: $quantity
      ) {
        id
        quantity {
          value
          unit
        }
      }
      upsertSellerProductConfiguration(
        platformProductLengthId: $platformProductsLengthId
        scope: $scope
        facilityId: $facilityId
        sellerProductConfigurationInput: $sellerProductConfigurationInput
      ) {
        id
        scope
        facility {
          id
          code
        }
        criticalStockLevel {
          value
          unit
        }
        criticalStockLevelInPieces: criticalStockLevel(
          mode: { mode: SPECIFIC, unit: PCS }
        ) {
          value
          unit
        }
        internalNotes
        lowStockLevel {
          value
          unit
        }
        lowStockLevelInPieces: lowStockLevel(
          mode: { mode: SPECIFIC, unit: PCS }
        ) {
          value
          unit
        }
        minimumOrderQuantity {
          value
          unit
        }
        readyToShipDays
        replenishmentRatePerWeek {
          value
          unit
        }
        status
      }
    }
  `);

  return useGraphqlMutation(upsertInventoryAndConfigMutation, [
    'my-products',
    'sellerProductInventories',
  ]);
};

export const useSetSellerProductPrice = () => {
  const setSellerProductPriceMutation = graphql(`
    mutation SetSellerProductPrice($input: SellerProductPriceInput!) {
      setSellerProductPrice(input: $input) {
        id
        platformProductLength {
          id
        }
        materialCost {
          amount
          currency
        }
        margin
        pricePerUnit {
          amount
          currency
        }
        currencyCode
        unit
      }
    }
  `);

  return useGraphqlMutation(setSellerProductPriceMutation, [
    'my-products',
    'sellerProductInventories',
    'sellerCatalogPrices',
  ]);
};

export const useGetSellerCatalogPrices = (
  page: number,
  size: number,
  filters?: SellerCatalogPriceFiltersInput,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query sellerCatalogPrices(
      $page: Int!
      $size: Int!
      $filters: SellerCatalogPriceFiltersInput
    ) {
      sellerCatalogPrices(
        pageInput: { page: $page, size: $size }
        filters: $filters
      ) {
        paging {
          totalPages
          totalElements
          size
          number
        }
        content {
          id
          lengthDisplayValue
          platformProduct {
            id
            name
            code
            category
          }
          myPrice {
            id
            materialCost {
              amount
              currency
            }
            margin
            pricePerUnit {
              amount
              currency
            }
            currencyCode
            unit
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'sellerCatalogPrices',
      page.toString(),
      size.toString(),
      JSON.stringify(filters || null),
    ],
    { page, size, filters },
    options
  );
};

export const useGetUniqueLocationsWithInventory = () => {
  const query = graphql(`
    query UniqueLocationsWithInventory {
      uniqueLocationsWithInventory {
        id
        name
        code
        branchCode
        facility {
          address {
            addressLine1
            city
            region
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['uniqueLocationsWithInventory']);
};

export const useGetMyProductFilterOptions = () => {
  const query = graphql(`
    query MyProductFilterOptions {
      platformProductDimensions {
        nominalThicknessInches
        nominalWidthInches
      }
    }
  `);

  return useGraphqlQuery(query, ['myProductFilterOptions']);
};
